/**
 * @fileoverview MicrophoneCheckRenderer component for the Storyteller web application.
 * Renders a microphone check interface with recording capabilities and error handling.
 *
 * @example
 * // Basic usage
 * <MicrophoneCheckRenderer
 *   uischema={{
 *     options: {
 *       audio: "test.mp3",
 *       captions: {
 *         default: "Click to start",
 *         playing: "Playing...",
 *         recording: "Recording..."
 *       },
 *       recordingStartMode: "manual",
 *       recordingStopMode: "manual"
 *     }
 *   }}
 * />
 */


import { StatePropsOfRenderer, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsLabelProps } from '@jsonforms/react'
import { withVanillaControlProps } from '@jsonforms/vanilla-renderers'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MiddlewareContext } from '../../../../MiddlewareContext'
import { useQCErrors } from '../../../../hooks/useQCErrors'
import { NavigationButton } from '../../../NavigationButton'
import { StreamingRecorder, StreamingState } from '../../../StreamingRecorder/StreamingRecorder'
import { Instructions } from '../../sections/Instructions'
import { Title } from '../../sections/Title'

import css from './MicrophoneCheck.module.css'

/**
 * @function getError
 * @description Helper function to extract the first error from a record of errors.
 * @param {Record<string, string>} errors - The record of errors.
 * @returns {Novoic.ValidationError} The first error.
 */
function getError(errors: Record<string, string>): Novoic.ValidationError {
    const name = Object.keys(errors)[0]
    const error = errors[name]
    return {
        name,
        error,
    }
}

/**
 * @function getCaption
 * @description Helper function to get the caption based on the streaming state.
 * @param {Record<string, string>} captions - The record of captions.
 * @param {StreamingState} state - The streaming state.
 * @returns {string} The caption.
 */
function getCaption(captions: Record<string, string>, state: StreamingState): string {
    switch (state) {
        case StreamingState.IDLE:
        case StreamingState.LOADING:
            return captions.default
        case StreamingState.PLAYING:
            return captions.playing
        case StreamingState.STREAMING:
            return captions.recording
        case StreamingState.PROCESSING:
        default:
            return ''
    }
}

/**
 * @function MicrophoneCheckRenderer
 * @description Component for rendering a microphone check interface with recording capabilities and error handling.
 * @param {StatePropsOfRenderer} props - The props for the MicrophoneCheckRenderer component.
 * @returns {JSX.Element} The MicrophoneCheckRenderer component.
 */
function MicrophoneCheckRenderer(props: StatePropsOfRenderer) {
    const { audio, captions, recordingStartMode, recordingStopMode } = props.uischema.options

    const { t } = useTranslation()
    const { exit, submit: onSubmit, question } = useContext(MiddlewareContext)
    const [store, retrieve] = useQCErrors(question?.id)
    const [storedAttempt, storedErrors, errorCount] = retrieve()
    const [attempt, setAttempt] = useState<number>(storedAttempt)
    const [state, setState] = useState<StreamingState>(StreamingState.IDLE)
    const [showErrors, setShowErrors] = useState<boolean>(Boolean(storedErrors))
    const params = new URLSearchParams(window.location.search)

    /**
     * Submit when streaming is finished
     */
    async function onStreamingEnded(recording: string) {
        const errors = await onSubmit(question.id, { recording, skip: attempt === 2 })
        if (errors) {
            store(attempt, getError(errors as Record<string, string>))
            setState(StreamingState.IDLE)
            setAttempt(attempt + 1)
            setShowErrors(true)
        }
    }

    /**
     * Change the state of the streaming so UI can be updated based on it
     */
    function onStateChange(state: StreamingState) {
        setState(state)
    }

    if (showErrors && storedErrors) {
        const idx = errorCount - 1
        const title = t(`errors.${storedErrors.name}.${idx}.title`)
        const instructions = t(`errors.${storedErrors.name}.${idx}.instructions`)
        const button = t(`errors.${storedErrors.name}.${idx}.button`)

        return (
            <div className={css.error_container}>
                <Title>{title}</Title>
                <Instructions instructions={instructions} />

                <div className={css.button_container}>
                    <NavigationButton
                        type="confirm"
                        label={button}
                        onClick={async () => {
                            setShowErrors(false)
                        }}
                    />
                </div>
            </div>
        )
    }

    return (
        <div className={css.container}>
            <Title flexible>{t(getCaption(captions, state))}</Title>

            <StreamingRecorder
                skippable={params.get('debug') === 'true'}
                attempt={attempt}
                onStreamingEnded={onStreamingEnded}
                onStateChange={onStateChange}
                onExit={exit}
                audio={audio}
                recordingStartMode={recordingStartMode}
                recordingStopMode={recordingStopMode}
            />
        </div>
    )
}

export const MicrophoneCheckTester = rankWith(3, uiTypeIs('MicrophoneCheck'))

export default withVanillaControlProps(withJsonFormsLabelProps(MicrophoneCheckRenderer))
